/**
 * Grand Quack Auto — Mother's Rage post-process shader.
 *
 * Vignette + chromatic aberration that ramps up when a duckling is killed.
 * Driven by a single uniform `uStrength ∈ [0,1]`; juiceSystem tweens it.
 *
 * - Chromatic aberration shifts R/B channels along the radial direction by a
 *   tiny offset proportional to strength. Sells the "vision is glitching"
 *   sensation instantly.
 * - Vignette darkens edges and blends them toward deep red. Doubles as
 *   "tunnel vision" + "everything just went red". Maximum readable for clips.
 */

import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';

const RAGE_SHADER = {
  uniforms: {
    tDiffuse: { value: null },
    uStrength: { value: 0 },
    uHaze: { value: 0 },
    uTime: { value: 0 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float uStrength;
    uniform float uHaze;
    uniform float uTime;
    varying vec2 vUv;

    void main() {
      vec2 uv = vUv;
      vec2 dir = uv - 0.5;
      float dist = length(dir);

      // Heat haze distortion — radial sine wave, strongest near center, decays outward.
      float hazeWave = sin(uv.y * 40.0 + uTime * 12.0) * sin(uv.x * 30.0 + uTime * 8.0);
      uv += dir * hazeWave * uHaze * 0.008 * smoothstep(0.5, 0.0, dist);

      // Chromatic aberration: shift R/B channels radially.
      float caShift = uStrength * 0.012;
      float r = texture2D(tDiffuse, uv + dir * caShift).r;
      float g = texture2D(tDiffuse, uv).g;
      float b = texture2D(tDiffuse, uv - dir * caShift).b;
      vec3 color = vec3(r, g, b);

      // Vignette tinted toward deep red.
      float vignette = smoothstep(0.25, 0.85, dist);
      color = mix(color, vec3(0.45, 0.0, 0.05), vignette * uStrength * 0.85);

      gl_FragColor = vec4(color, 1.0);
    }
  `,
};

let activePass: ShaderPass | null = null;

/** Build the rage pass. Call once at boot, insert into composer before OutputPass. */
export function createRageFxPass(): ShaderPass {
  const pass = new ShaderPass(RAGE_SHADER);
  activePass = pass;
  return pass;
}

/** Set rage strength ∈ [0,1]. Called by juiceSystem each render frame. */
export function setRageStrength(strength: number): void {
  if (!activePass) return;
  if (activePass.uniforms.uStrength) activePass.uniforms.uStrength.value = strength;
}

/** Set heat haze intensity ∈ [0,1]. Called by juiceSystem on mega-quack. */
export function setHazeStrength(haze: number): void {
  if (!activePass) return;
  if (activePass.uniforms.uHaze) activePass.uniforms.uHaze.value = haze;
}

/** Update shader time. Called by juiceSystem each render frame. */
export function setShaderTime(t: number): void {
  if (!activePass) return;
  if (activePass.uniforms.uTime) activePass.uniforms.uTime.value = t;
}
